import { useCallback } from "react";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import {
  createFrontendRequestId,
  type FrontendRequestFailureContext,
} from "../../../services/frontendErrorReporting";

export type CollaborationUserRole = "owner" | "collaborator" | string;

/** API-owned appearance. Unassigned agents have no cut or colour. */
export interface AgentAvatarOverlay {
  status?: "assigned" | "unassigned";
  cut?: string;
  colour?: string;
  reservationActive?: boolean;
  recoveryRequired?: boolean;
}

export interface AgentAvatarSelection {
  selected_cut: string;
  selected_colour: string;
}

/** Occupied values from other active agents; the client owns its palette. */
export interface AgentAvatarReservations {
  reservedCuts: string[];
  reservedColours: string[];
}

export interface CollaborationConversationSummary {
  id: number;
  externalId: string | null;
  title: string | null;
  kind: "chat" | "agent" | string;
  agentName: string | null;
  systemPromptOverride: string | null;
  agentStatus: string;
  agentLastHeartbeatAt: string | null;
  agentCurrentTask: string | null;
  messageCount: number;
  lastMessageAt: string | null;
  isArchived: boolean;
  decommissionedAt: string | null;
  createdAt: string;
  updatedAt: string | null;
  userRole: CollaborationUserRole | null;
  archivedReason: string | null;
  avatar?: AgentAvatarOverlay;
}

export interface CollaborationConversationListResponse {
  items: CollaborationConversationSummary[];
  total: number;
  skip: number;
  limit: number;
}

export interface ConversationCollaborator {
  user_id: string;
  display_name: string;
  email: string;
  role: CollaborationUserRole;
  added_at: string;
  added_by: string | null;
}

interface RawConversationListResponse {
  items?: unknown[];
  total?: number;
  skip?: number;
  limit?: number;
}

interface RawCollaboratorListResponse {
  collaborators?: unknown[];
}

const asObject = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const normalizeAvatarOverlay = (
  value: unknown,
): AgentAvatarOverlay | undefined => {
  const avatar = asObject(value);
  if (avatar?.status === "unassigned") return { status: "unassigned" };
  if (
    !avatar ||
    typeof avatar.cut !== "string" ||
    typeof avatar.colour !== "string" ||
    typeof avatar.reservation_active !== "boolean" ||
    typeof avatar.recovery_required !== "boolean"
  ) {
    return undefined;
  }
  return {
    status: "assigned",
    cut: avatar.cut,
    colour: avatar.colour,
    reservationActive: avatar.reservation_active,
    recoveryRequired: avatar.recovery_required,
  };
};

export const normalizeConversationSummary = (
  value: unknown,
): CollaborationConversationSummary | null => {
  const item = asObject(value);
  if (!item) return null;

  const rawId = item.id;
  const id =
    typeof rawId === "number"
      ? rawId
      : typeof rawId === "string"
        ? Number.parseInt(rawId, 10)
        : NaN;
  if (!Number.isFinite(id)) return null;

  // Older Hub projections can return a nullable kind while retaining the
  // agent fields and avatar overlay. A real string is authoritative (so an
  // explicit "chat" never becomes an agent); only absent/non-string kinds
  // use the same non-null agent metadata fallback as the API.
  const hasAgentMetadata =
    (item.agent_name !== null && item.agent_name !== undefined) ||
    (item.agent_status !== null && item.agent_status !== undefined) ||
    (item.system_prompt_override !== null &&
      item.system_prompt_override !== undefined);
  const kind =
    typeof item.kind === "string"
      ? item.kind
      : hasAgentMetadata
        ? "agent"
        : "chat";

  return {
    id,
    externalId: typeof item.external_id === "string" ? item.external_id : null,
    title: typeof item.title === "string" ? item.title : null,
    kind,
    agentName: typeof item.agent_name === "string" ? item.agent_name : null,
    systemPromptOverride:
      typeof item.system_prompt_override === "string"
        ? item.system_prompt_override
        : null,
    agentStatus:
      typeof item.agent_status === "string" ? item.agent_status : "idle",
    agentLastHeartbeatAt:
      typeof item.agent_last_heartbeat_at === "string"
        ? item.agent_last_heartbeat_at
        : null,
    agentCurrentTask:
      typeof item.agent_current_task === "string"
        ? item.agent_current_task
        : null,
    messageCount:
      typeof item.message_count === "number" ? item.message_count : 0,
    lastMessageAt:
      typeof item.last_message_at === "string" ? item.last_message_at : null,
    isArchived: Boolean(item.is_archived),
    decommissionedAt:
      typeof item.decommissioned_at === "string"
        ? item.decommissioned_at
        : null,
    createdAt:
      typeof item.created_at === "string"
        ? item.created_at
        : typeof item.updated_at === "string"
          ? item.updated_at
          : new Date(0).toISOString(),
    updatedAt: typeof item.updated_at === "string" ? item.updated_at : null,
    userRole: typeof item.user_role === "string" ? item.user_role : null,
    archivedReason:
      typeof item.archived_reason === "string"
        ? item.archived_reason
        : typeof item.archive_reason === "string"
          ? item.archive_reason
          : null,
    avatar: normalizeAvatarOverlay(item.avatar),
  };
};

export const normalizeConversationListResponse = (
  response: unknown,
): CollaborationConversationListResponse => {
  const payload = (response || {}) as RawConversationListResponse;
  const items = Array.isArray(payload.items)
    ? payload.items
        .map(normalizeConversationSummary)
        .filter(
          (item): item is CollaborationConversationSummary => item !== null,
        )
    : [];

  return {
    items,
    total: typeof payload.total === "number" ? payload.total : items.length,
    skip: typeof payload.skip === "number" ? payload.skip : 0,
    limit: typeof payload.limit === "number" ? payload.limit : items.length,
  };
};

export const normalizeCollaboratorListResponse = (
  response: unknown,
): ConversationCollaborator[] => {
  const payload = (response || {}) as RawCollaboratorListResponse;
  if (!Array.isArray(payload.collaborators)) return [];

  return payload.collaborators
    .map((value) => {
      const item = asObject(value);
      if (!item || typeof item.user_id !== "string") return null;
      return {
        user_id: item.user_id,
        display_name:
          typeof item.display_name === "string"
            ? item.display_name
            : typeof item.email === "string"
              ? item.email
              : item.user_id,
        email: typeof item.email === "string" ? item.email : item.user_id,
        role: typeof item.role === "string" ? item.role : "collaborator",
        added_at: typeof item.added_at === "string" ? item.added_at : "",
        added_by: typeof item.added_by === "string" ? item.added_by : null,
      } satisfies ConversationCollaborator;
    })
    .filter((item): item is ConversationCollaborator => item !== null);
};

export const useChatConversationSummaries = (
  onRequestFailure?: (context: FrontendRequestFailureContext) => void,
) => {
  // Keep the Response so successful JSON-read failures retain the HTTP status.
  const authFetch = useAuthFetch("response");

  return useCallback(
    async ({
      workspaceId,
      skip = 0,
      limit = 25,
      search,
      includeDecommissioned = false,
    }: {
      workspaceId: string;
      skip?: number;
      limit?: number;
      search?: string;
      includeDecommissioned?: boolean;
    }): Promise<CollaborationConversationListResponse> => {
      const params = new URLSearchParams();
      params.set("skip", String(skip));
      params.set("limit", String(limit));
      if (search?.trim()) {
        params.set("search", search.trim());
      }
      if (includeDecommissioned) {
        params.set("include_decommissioned", "true");
      }

      const requestId = createFrontendRequestId();
      const headers = new Headers({ "X-Workspace-Id": workspaceId });
      headers.set("X-Request-ID", requestId);
      const reportFailure = (context: FrontendRequestFailureContext) => {
        try {
          onRequestFailure?.(context);
        } catch {
          // Diagnostics must not replace the original request failure.
        }
      };

      try {
        const response = await authFetch(
          `/api/workspace/ida/chat/conversations?${params.toString()}`,
          "fetch chat conversations",
          { headers },
        );
        // Preserve useAuthFetch's existing 204-as-empty-list behavior.
        if (response === null) return normalizeConversationListResponse(null);

        try {
          const payload = await (response as Response).json();
          return normalizeConversationListResponse(payload);
        } catch (error) {
          reportFailure({
            requestId,
            kind: "history_get",
            phase: "response_read",
            status: (response as Response).status,
          });
          throw error;
        }
      } catch (error) {
        const failure = error as { code?: unknown; status?: unknown };
        if (
          typeof failure.status === "number" &&
          Number.isInteger(failure.status) &&
          failure.status >= 100 &&
          failure.status <= 599 &&
          (failure.status < 200 || failure.status >= 300)
        ) {
          reportFailure({
            requestId,
            kind: "history_get",
            phase: "http_response",
            status: failure.status,
          });
        } else if (
          failure.code === "network_or_cors" ||
          (error instanceof Error && error.name === "TimeoutError")
        ) {
          reportFailure({
            requestId,
            kind: "history_get",
            phase: "fetch_rejected",
          });
        }
        throw error;
      }
    },
    [authFetch, onRequestFailure],
  );
};

/** Create the empty conversation used before a first-message or voice session. */
export async function createEmptyChatConversation(
  authFetch: ReturnType<typeof useAuthFetch>,
  workspaceId: string,
): Promise<number> {
  const response = await authFetch(
    "/api/workspace/ida/chat/conversations",
    "create chat conversation",
    {
      method: "POST",
      headers: { "X-Workspace-Id": workspaceId },
    },
  );
  const conversationId = response?.conversation_id;
  if (
    !Number.isSafeInteger(conversationId) ||
    typeof conversationId !== "number" ||
    conversationId <= 0
  ) {
    throw new Error("The server returned an invalid conversation ID.");
  }
  return conversationId;
}

export const useCreateEmptyChatConversation = () => {
  const authFetch = useAuthFetch();
  return useCallback(
    (workspaceId: string) =>
      createEmptyChatConversation(authFetch, workspaceId),
    [authFetch],
  );
};

export const useConversationCollaborators = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      conversationId: number,
    ): Promise<ConversationCollaborator[]> => {
      const response = await authFetch(
        `/api/workspace/ida/chat/conversations/${conversationId}/collaborators`,
        "fetch conversation collaborators",
        {
          headers: {
            "X-Workspace-Id": workspaceId,
          },
        },
      );
      return normalizeCollaboratorListResponse(response);
    },
    [authFetch],
  );
};

export const useAddConversationCollaborators = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      conversationId: number,
      userIds: string[],
    ): Promise<ConversationCollaborator[]> => {
      const response = await authFetch(
        `/api/workspace/ida/chat/conversations/${conversationId}/collaborators`,
        "add conversation collaborators",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Workspace-Id": workspaceId,
          },
          body: JSON.stringify({ user_ids: userIds }),
        },
      );
      return normalizeCollaboratorListResponse(response);
    },
    [authFetch],
  );
};

export const useRemoveConversationCollaborator = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      conversationId: number,
      userId: string,
    ): Promise<void> => {
      await authFetch(
        `/api/workspace/ida/chat/conversations/${conversationId}/collaborators/${encodeURIComponent(
          userId,
        )}`,
        "remove conversation collaborator",
        {
          method: "DELETE",
          headers: {
            "X-Workspace-Id": workspaceId,
          },
        },
      );
    },
    [authFetch],
  );
};

export const useDeleteChatConversation = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (workspaceId: string, conversationId: number): Promise<void> => {
      await authFetch(
        `/api/workspace/ida/chat/conversations/${conversationId}`,
        "delete conversation",
        {
          method: "DELETE",
          headers: {
            "X-Workspace-Id": workspaceId,
          },
        },
      );
    },
    [authFetch],
  );
};

/**
 * Rename an IDA conversation by setting a new title.
 *
 * The BFF enforces non-empty title (Pydantic min_length=1, max_length=255);
 * the hub enforces owner-or-member authorization via require_chat_write.
 * Callers should still pre-validate non-empty on the FE for snappier UX
 * (no round-trip on obviously invalid input) and roll back optimistic
 * local state if the response is not OK.
 */
export const useRenameChatConversation = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      conversationId: number,
      title: string,
    ): Promise<void> => {
      await authFetch(
        `/api/workspace/ida/chat/conversations/${conversationId}`,
        "rename conversation",
        {
          method: "PATCH",
          headers: {
            "X-Workspace-Id": workspaceId,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ title }),
        },
      );
    },
    [authFetch],
  );
};

export interface AgentConversationPatch {
  agent_name?: string;
  system_prompt_override?: string | null;
  agent_status?: string;
  agent_current_task?: string | null;
  /**
   * Decommission / resurrect an agent. Set to an ISO 8601 timestamp to flag
   * decommissioned. Set explicitly to `null` to resurrect (clear the flag).
   * Omit the key entirely to leave the field untouched -- the BE
   * distinguishes "field absent" from "field set to null" via Pydantic's
   * `model_fields_set`.
   */
  decommissioned_at?: string | null;
  /** Copies the selected library persona into the agent by value on the server. */
  persona_source_id?: string;
}

export interface ReusablePersona {
  id: string;
  name: string;
  body: string;
  is_shared: boolean;
  version: number;
  owner_user_id: string;
  updated_at: string;
  is_update_available: boolean;
}

export interface ReusablePersonaGroups {
  own: ReusablePersona[];
  shared: ReusablePersona[];
}

export const usePersonaLibrary = () => {
  const authFetch = useAuthFetch();

  const list = useCallback(
    async (
      workspaceId: string,
      agentConversationId?: number,
    ): Promise<ReusablePersonaGroups> => {
      const query =
        agentConversationId === undefined
          ? ""
          : `?agent_conversation_id=${agentConversationId}`;
      const response = await authFetch(
        `/api/workspace/ida/personas${query}`,
        "fetch reusable personas",
        {
          headers: { "X-Workspace-Id": workspaceId },
        },
      );
      const payload = asObject(response) ?? {};
      return {
        own: Array.isArray(payload?.own)
          ? (payload.own as ReusablePersona[])
          : [],
        shared: Array.isArray(payload?.shared)
          ? (payload.shared as ReusablePersona[])
          : [],
      };
    },
    [authFetch],
  );

  const create = useCallback(
    async (
      workspaceId: string,
      input: Pick<ReusablePersona, "name" | "body" | "is_shared">,
    ): Promise<ReusablePersona> => {
      const response = await authFetch(
        "/api/workspace/ida/personas",
        "create reusable persona",
        {
          method: "POST",
          headers: {
            "X-Workspace-Id": workspaceId,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(input),
        },
      );
      return response as ReusablePersona;
    },
    [authFetch],
  );

  const update = useCallback(
    async (
      workspaceId: string,
      personaId: string,
      input: Pick<ReusablePersona, "name" | "body" | "is_shared">,
    ): Promise<ReusablePersona> => {
      const response = await authFetch(
        `/api/workspace/ida/personas/${encodeURIComponent(personaId)}`,
        "update reusable persona",
        {
          method: "PATCH",
          headers: {
            "X-Workspace-Id": workspaceId,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(input),
        },
      );
      return response as ReusablePersona;
    },
    [authFetch],
  );

  const remove = useCallback(
    async (workspaceId: string, personaId: string): Promise<void> => {
      await authFetch(
        `/api/workspace/ida/personas/${encodeURIComponent(personaId)}`,
        "delete reusable persona",
        {
          method: "DELETE",
          headers: { "X-Workspace-Id": workspaceId },
        },
      );
    },
    [authFetch],
  );

  return { list, create, update, remove };
};

export const useUpdateAgentConversation = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      conversationId: number,
      patch: AgentConversationPatch,
    ): Promise<CollaborationConversationSummary | null> => {
      const response = await authFetch(
        `/api/workspace/ida/chat/conversations/${conversationId}/agent`,
        "update agent persona",
        {
          method: "PATCH",
          headers: {
            "X-Workspace-Id": workspaceId,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(patch),
        },
      );
      return normalizeConversationSummary(response);
    },
    [authFetch],
  );
};

/** Owner-only recovery action for an agent without an active appearance. */
export const useAssignAgentAvatar = () => {
  const authFetch = useAuthFetch();
  return useCallback(
    async (
      workspaceId: string,
      conversationId: number,
      selection: AgentAvatarSelection,
    ) =>
      normalizeConversationSummary(
        await authFetch(
          `/api/workspace/ida/chat/conversations/${conversationId}/agent/avatar`,
          "assign agent appearance",
          {
            method: "PUT",
            headers: {
              "X-Workspace-Id": workspaceId,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(selection),
          },
        ),
      ),
    [authFetch],
  );
};

/** Owner-authorized, unpaged workspace reservation snapshot for the picker. */
export const useAgentAvatarReservations = () => {
  const authFetch = useAuthFetch();
  return useCallback(
    async (
      workspaceId: string,
      conversationId: number,
    ): Promise<AgentAvatarReservations> => {
      const response = await authFetch(
        `/api/workspace/ida/chat/conversations/${conversationId}/agent/avatar/availability`,
        "get agent appearance availability",
        { headers: { "X-Workspace-Id": workspaceId } },
      );
      const payload = asObject(response);
      const strings = (value: unknown): string[] =>
        Array.isArray(value)
          ? value.filter((item): item is string => typeof item === "string")
          : [];
      return {
        reservedCuts: strings(payload?.reserved_cuts),
        reservedColours: strings(payload?.reserved_colours),
      };
    },
    [authFetch],
  );
};

/** Owner-only release action, valid only after the server confirms decommissioning. */
export const useReleaseAgentAvatar = () => {
  const authFetch = useAuthFetch();
  return useCallback(
    async (workspaceId: string, conversationId: number) =>
      normalizeConversationSummary(
        await authFetch(
          `/api/workspace/ida/chat/conversations/${conversationId}/agent/avatar/release`,
          "release agent appearance",
          { method: "POST", headers: { "X-Workspace-Id": workspaceId } },
        ),
      ),
    [authFetch],
  );
};
